// writeBackDocumentPaymentRollups.js
//
// Persist amountPaid / balanceDue / documentStatus on Invoice or Proposal
// after payment schedule changes.

import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { updateNode } from 'services/nodes/NodeWriteService';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import {
  deriveDocumentArStatus,
} from 'nodetypes/documents/payments/documentPaymentsModel';
import { readInvoiceField } from 'nodetypes/invoice/view/invoiceGeneralInfoModel';
import { computeInvoiceItemsRollups } from 'nodetypes/invoice/view/invoiceItemsModel';
import { readProposalField } from 'nodetypes/proposal/view/proposalGeneralInfoModel';
import { computeProposalItemsRollups } from 'nodetypes/proposal/view/proposalItemsModel';

function normalizeTypeKey(value) {
  return String(value || '').trim().toUpperCase();
}

function readParentField(parentNode, fieldName, parentTypeKey, parentType) {
  if (parentTypeKey === 'INVOICE') {
    return readInvoiceField(parentNode, fieldName, parentType);
  }
  if (parentTypeKey === 'PROPOSAL') {
    return readProposalField(parentNode, fieldName, parentType);
  }
  return undefined;
}

function computeParentRollups(parentTypeKey, items, parentNode, parentType, payments) {
  if (parentTypeKey === 'INVOICE') {
    return computeInvoiceItemsRollups(items, parentNode, parentType, payments);
  }
  if (parentTypeKey === 'PROPOSAL') {
    return computeProposalItemsRollups(items, parentNode, parentType, payments);
  }
  return null;
}

/**
 * Write amountPaid / balanceDue / documentStatus from payment children.
 *
 * @param {object} options
 * @param {object} options.parentNode
 * @param {object|null} [options.parentType]
 * @param {object[]} [options.items]
 * @param {object[]} [options.payments]
 * @returns {Promise<object|null>} Patch applied, or null
 */
export async function writeBackDocumentPaymentRollups({
  parentNode,
  parentType = null,
  items = [],
  payments = [],
}) {
  if (!parentNode?.key) return null;

  const parentTypeKey = normalizeTypeKey(parentType?.key || parentNode.type);
  if (parentTypeKey !== 'INVOICE' && parentTypeKey !== 'PROPOSAL') return null;

  const resolvedType = parentType || getNodeType(parentTypeKey);
  const rollups = computeParentRollups(
    parentTypeKey,
    items,
    parentNode,
    resolvedType,
    payments,
  );
  if (!rollups) return null;

  const amountPaid = Math.round(Number(rollups.amountPaid) || 0);
  const balanceDue = Math.round(Number(rollups.balanceDue) || 0);
  const currentStatus = readParentField(
    parentNode,
    'documentStatus',
    parentTypeKey,
    resolvedType,
  );
  const nextStatus = deriveDocumentArStatus({
    currentStatus,
    total: rollups.total,
    amountPaid,
    balanceDue,
  });

  const patch = {};
  const mergeField = (fieldName, value) => {
    const fieldPatch = buildNodeFieldPatch(
      { ...parentNode, ...patch },
      {
        name: fieldName,
        source: parentTypeKey.toLowerCase(),
        type: fieldName === 'documentStatus' ? 'enum' : 'money',
      },
      parentTypeKey,
      value,
    );
    if (!fieldPatch) return;
    Object.entries(fieldPatch).forEach(([sectionKey, sectionValue]) => {
      patch[sectionKey] = {
        ...(patch[sectionKey] || {}),
        ...sectionValue,
      };
    });
  };

  mergeField('amountPaid', amountPaid);
  mergeField('balanceDue', balanceDue);
  if (nextStatus) mergeField('documentStatus', nextStatus);

  if (!Object.keys(patch).length) return null;
  await updateNode(parentNode.key, patch);
  return patch;
}
